// src/project/data/triggers/saveKyc.js
// Flow KYC V3 en 3 étapes :
// 1. POST /kyc/create/draft  → crée le brouillon (ID déterministe : kyc_rider_{userId})
// 2. POST /kyc/update/{kycId} → rempli les données
// 3. POST /kyc/submit/{kycId} → soumet pour validation
import { buildActionUrl, RIDER_ENDPOINTS, getUserId } from '../config/apiConfig'
import Storage from '@/shared/utils/storage'

const formatDate = (dateStr) => {
  if (!dateStr) return null
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return null
  return date.toISOString().split('T')[0]
}

const idTypeMap = {
  "Carte nationale d'identité": 'national_id',
  Passeport: 'passport',
  'Permis de conduire': 'driving_license',
  Autre: 'national_id',
}

export default {
  postUrl() {
    // Step 1: Créer un brouillon KYC (l'ID est déterministe)
    const userId = getUserId()
    return buildActionUrl(`${RIDER_ENDPOINTS.KYC_CREATE_DRAFT}&userId=${userId}`)
  },
  format(payload) {
    const userId = getUserId()
    const kycId = `kyc_rider_${userId}`
    const nameParts = (payload.fullName || '').trim().split(/\s+/)
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || ''
    const userData = Storage.get('userData')

    // Step 1 body : Créer le brouillon
    return {
      id: kycId,
      userId,
      userType: 'rider',
      data: {
        personalInfo: {
          firstName,
          lastName,
          phone: payload.phoneNumber?.replace(/[\s\-\(\)]/g, '') || userData?.phone || '',
          dateOfBirth: formatDate(payload.birthDate),
          nationality: payload.nationality || '',
          address: payload.address || '',
          city: payload.city || '',
        },
        identityDocument: payload.idType
          ? {
              type: idTypeMap[payload.idType] || 'national_id',
              number: payload.idNumber || '',
              frontPhotoUrl: payload.idFrontPhotoUrl || '',
              backPhotoUrl: payload.idBackPhotoUrl || '',
              expirationDate: formatDate(payload.idExpiryDate),
            }
          : {},
        vehicleDocument: payload.vehicleRegistration
          ? {
              type: 'registration',
              number: payload.vehicleRegistration || '',
              photoUrl: payload.vehiclePhotoUrl || '',
            }
          : {},
        selfieUrl: payload.selfieUrl || '',
      },
      // Stocker le payload brut pour les étapes suivantes
      _rawPayload: payload,
    }
  },
  async afterSuccess(result, payload) {
    // Cette méthode est appelée si le service supporte les post-processing.
    // Elle réalise les étapes 2 et 3 après la création du draft.
    // Note: Si le framework ne supporte pas afterSuccess, les steps 2&3
    // doivent être gérées séparément via des triggers dédiés.
    return result
  },
  parse(raw = {}) {
    // Réponse V3 : 201 (créé) ou 200 (déjà existant) sont tous les deux valides
    if (raw?.statusCode !== 200 && raw?.statusCode !== 201) {
      return {
        statusCode: raw?.statusCode || 500,
        data: { saved: false, status: 'error', message: raw?.message || 'Failed to save KYC' },
      }
    }

    const apiData = raw?.data || {}
    const statusField = apiData?.status

    let appStatus = 'not_filled'
    switch (statusField) {
      case 'draft':
      case 'not_started':
      case 'not_submitted':
        appStatus = 'not_filled'
        break
      case 'pending':
      case 'submitted':
        appStatus = 'pending'
        break
      case 'approved':
      case 'verified':
        appStatus = 'validated'
        break
      case 'rejected':
        appStatus = 'rejected'
        break
      default:
        appStatus = 'pending' // Par défaut après soumission
    }

    return {
      statusCode: 200,
      data: {
        saved: true,
        status: appStatus,
        kyc: apiData,
        kycId: apiData?.id || null,
        message: 'KYC enregistré avec succès',
      },
    }
  },
}
